import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { publicacaoService } from '../publicacaoService';
import { MAXIMO_FIXADAS, estaFixada } from '../publicacao.definitions';
import type { Publicacao } from '../publicacao.types';
import { PUBLICACOES_QUERY_KEYS, usePessoaLogada } from '../usePublicacoes';
import { editavelDe, errosDoForm } from './publicacaoEditor';
import type { FormPublicacao } from './publicacaoEditor';

/** 'rascunho' = salvar sem publicar; 'publicar' = publicar agora ou agendar (form.agendar);
 * 'salvar' = só salvar (já publicada/despublicada). */
export type AcaoSalvar = 'rascunho' | 'publicar' | 'salvar';

/**
 * Salva o editor numa sequência só: cria o rascunho (se novo) → sobe capa/anexos (precisam do
 * id) → resolve o público da ciência (projetos → pessoas, no servidor) → grava → apaga do Storage
 * o que foi removido → publica/agenda/volta pra rascunho conforme a ação.
 */
export function useSalvarPublicacao(todas: Publicacao[]) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { autor } = usePessoaLogada();
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState<string[]>([]);

  /** `idNovo`: id já reservado pro rascunho novo (os anexos subiram nessa pasta). */
  const salvar = async (form: FormPublicacao, existente: Publicacao | null, acao: AcaoSalvar, idNovo: string): Promise<string | null> => {
    if (!autor) return null;
    const validacao = errosDoForm(form, acao === 'publicar' || existente?.status === 'publicada');
    const agora = Date.now();
    const outrasFixadas = todas.filter((p) => p.id !== existente?.id && p.status === 'publicada' && estaFixada(p, agora)).length;
    if (form.fixar && !(existente && estaFixada(existente, agora)) && outrasFixadas >= MAXIMO_FIXADAS) {
      validacao.push(`Já existem ${MAXIMO_FIXADAS} publicações fixadas — desafixe uma antes.`);
    }
    setErros(validacao);
    if (validacao.length > 0) return null;

    setSalvando(true);
    try {
      const arquivos = { capaUrl: form.capaUrl, capaPath: form.capaPath, anexos: form.anexos };
      const id = existente?.id ?? (await publicacaoService.criar(editavelDe(form, arquivos, []), autor, idNovo));

      let alvoIds: string[] = [];
      if (form.exigeCiencia && !form.cienciaPeloDocumento && form.publico === 'pessoas') alvoIds = form.pessoaIds;
      if (form.exigeCiencia && !form.cienciaPeloDocumento && form.publico === 'projetos') {
        alvoIds = (await publicacaoService.resolverPublicoPorProjeto(form.projetoIds)).map((c) => c.id);
      }

      await publicacaoService.salvar(id, editavelDe(form, arquivos, alvoIds), autor);
      await Promise.all(form.pathsRemovidos.map((path) => publicacaoService.removerArquivo(path)));

      if (acao === 'publicar') {
        await publicacaoService.publicar(id, form.agendar ? new Date(form.publicarEm) : null, autor);
        toast.success(form.agendar ? 'Publicação agendada.' : 'Publicado.');
      } else if (acao === 'rascunho') {
        if (existente?.status === 'agendada') await publicacaoService.voltarParaRascunho(id, autor);
        toast.success('Rascunho salvo.');
      } else {
        toast.success('Alterações salvas.');
      }
      if (form.exigeCiencia && !form.cienciaPeloDocumento && form.publico === 'projetos' && alvoIds.length === 0) {
        toast.warning('Ninguém está alocado nesses projetos hoje — a ciência não será cobrada de ninguém.');
      }
      return id;
    } catch (error) {
      toast.error(`Erro ao salvar: ${(error as { message?: string })?.message ?? 'erro desconhecido'}`);
      return null;
    } finally {
      setSalvando(false);
      queryClient.invalidateQueries({ queryKey: PUBLICACOES_QUERY_KEYS.all });
    }
  };

  return { salvar, salvando, erros, limparErros: () => setErros([]) };
}
